// src/services/marketplace.ts
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  getCountFromServer,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  startAfter,
  setDoc,
  updateDoc,
  where,
  documentId,
} from 'firebase/firestore';
import type { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import { auth, db, functions } from '../lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { FEATURE } from '../constants/features';
import type {
  MarketplaceItem,
  MarketplaceItemStatus,
  MarketplaceTransaction,
  Offer,
  OfferStatus,
} from '../types';

const ITEMS = 'marketplaceItems';
const TX = 'marketplaceTransactions';
const OFFERS_SUBCOLLECTION = 'offers';

export const MARKETPLACE_CATEGORIES = [
  'rods',
  'reels',
  'tackle',
  'electronics',
  'boats_kayaks',
  'apparel',
  'storage',
  'other',
] as const;

export const MARKETPLACE_CONDITIONS = [
  'new',
  'like_new',
  'good',
  'fair',
  'parts',
] as const;

export const MARKETPLACE_CURRENCIES = [
  'usd',
  'cad',
  'eur',
  'gbp',
  'aud',
  'nzd',
  'mxn',
  'php',
  'inr',
] as const;

function assertBazaarListingsEnabled(action: string) {
  if (!FEATURE.BAZAAR_LISTINGS_ONLY && !FEATURE.BAZAAR_ENABLED) {
    throw new Error(`${action} is temporarily closed while Bazaar is reviewed for launch.`);
  }
}

function stripUndefined(obj: Record<string, any>) {
  const out: Record<string, any> = {};
  Object.keys(obj || {}).forEach((key) => {
    if (obj[key] !== undefined) out[key] = obj[key];
  });
  return out;
}

function getItemDocId(itemId: number | string) {
  return String(itemId ?? '').trim();
}

function normalizeMoney(raw: any) {
  const priceCents =
    typeof raw?.priceCents === 'number'
      ? Math.max(0, Math.round(raw.priceCents))
      : typeof raw?.price === 'number'
        ? Math.max(0, Math.round(raw.price * 100))
        : 0;

  const currencyRaw = String(raw?.currency || 'usd').trim().toLowerCase();
  const currency = /^[a-z]{3}$/.test(currencyRaw) ? currencyRaw : 'usd';
  return { priceCents, currency };
}

function normalizeItem(raw: any, docId: string): MarketplaceItem {
  const id = String(docId || raw?.id || '').trim();
  const { priceCents, currency } = normalizeMoney(raw);

  const pickUrl = (value: any): string | null => {
    if (!value) return null;
    if (typeof value === 'string') return value.trim() || null;
    if (typeof value === 'object') {
      const u = value.url || value.downloadUrl || value.downloadURL || value.src;
      return typeof u === 'string' && u.trim() ? u.trim() : null;
    }
    return null;
  };

  const normalizeArray = (arr: any): string[] => {
    if (!Array.isArray(arr)) return [];
    return arr.map(pickUrl).filter((value): value is string => Boolean(value));
  };

  const imageUrls = normalizeArray(raw?.imageUrls);
  const images = imageUrls.length
    ? imageUrls
    : normalizeArray(raw?.images).length
      ? normalizeArray(raw?.images)
      : [pickUrl(raw?.imageUrl || raw?.image || raw?.photoUrl)].filter(
          (value): value is string => Boolean(value),
        );

  return {
    ...(raw || {}),
    id,
    docId,
    title: String(raw?.title || raw?.name || 'Untitled'),
    name: raw?.name,
    description: raw?.description,
    price: typeof raw?.price === 'number' ? raw.price : priceCents / 100,
    priceCents,
    currency,
    condition: raw?.condition,
    category: raw?.category || 'other',
    quantity: Math.max(0, Number(raw?.quantity ?? 1) || 0),
    images,
    imageUrls: images,
    imagePaths: Array.isArray(raw?.imagePaths) ? raw.imagePaths.filter(Boolean) : [],
    sellerId: String(raw?.sellerId || ''),
    sellerName: String(raw?.sellerName || 'Seller'),
    sellerUsername: raw?.sellerUsername,
    sellerAvatar: raw?.sellerAvatar,
    createdAt: raw?.createdAt ?? raw?.timestamp ?? 0,
    updatedAt: raw?.updatedAt ?? raw?.createdAt ?? 0,
    status: (raw?.status as MarketplaceItemStatus) ?? 'active',
    fulfillmentMethods: Array.isArray(raw?.fulfillmentMethods)
      ? raw.fulfillmentMethods
      : ['LOCAL_PICKUP'],
    checkoutMethods: Array.isArray(raw?.checkoutMethods)
      ? raw.checkoutMethods
      : ['DIRECT'],
    locationLabel: raw?.locationLabel,
    city: raw?.city,
    region: raw?.region,
    countryCode: raw?.countryCode,
  } as MarketplaceItem;
}

export function subscribeMarketplaceItems(
  onItems: (items: Record<string, MarketplaceItem>) => void,
  opts?: { max?: number },
) {
  const max = Math.max(1, Math.min(Number(opts?.max ?? 200), 500));
  const statuses = ['active', 'pending'] as const;
  const byStatus = new Map<string, Record<string, MarketplaceItem>>();

  const emit = () => {
    const merged: Record<string, MarketplaceItem> = {};
    for (const status of statuses) Object.assign(merged, byStatus.get(status) || {});
    onItems(merged);
  };

  const unsubscribers = statuses.map((status) => {
    const q = query(
      collection(db, ITEMS),
      where('schemaVersion', '==', 2),
      where('status', '==', status),
      orderBy('createdAt', 'desc'),
      limit(max),
    );

    return onSnapshot(q, (snap) => {
      const rows: Record<string, MarketplaceItem> = {};
      snap.forEach((row) => {
        const item = normalizeItem(row.data(), row.id);
        if ((item.quantity ?? 1) <= 0 || item.isSold) return;
        rows[row.id] = item;
      });
      byStatus.set(status, rows);
      emit();
    });
  });

  return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
}


export type MarketplaceSellerListingSection = 'active' | 'pending';
export type MarketplaceSellerHistoryCursor = QueryDocumentSnapshot<DocumentData>;

/**
 * Seller-only LIVE lifecycle projection for My listings.
 *
 * Keep only active + pending listings on realtime listeners. Historical sold /
 * archived inventory is intentionally paged on demand below so a long-time seller
 * never keeps hundreds of closed listings attached to live Firestore listeners.
 */
export function subscribeMarketplaceItemsForSeller(
  sellerId: string,
  onItems: (items: Record<string, MarketplaceItem>) => void,
  opts?: { maxPerSection?: number },
) {
  const uid = String(sellerId || '').trim();
  if (!uid) {
    onItems({});
    return () => {};
  }

  const maxPerSection = Math.max(1, Math.min(Number(opts?.maxPerSection ?? 100), 250));
  const sections: MarketplaceSellerListingSection[] = ['active', 'pending'];
  const bySection = new Map<MarketplaceSellerListingSection, Record<string, MarketplaceItem>>();

  const emit = () => {
    const merged: Record<string, MarketplaceItem> = {};
    for (const section of sections) Object.assign(merged, bySection.get(section) || {});
    onItems(merged);
  };

  const unsubscribers = sections.map((status) => {
    const q = query(
      collection(db, ITEMS),
      where('sellerId', '==', uid),
      where('status', '==', status),
      orderBy('createdAt', 'desc'),
      limit(maxPerSection),
    );

    return onSnapshot(q, (snap) => {
      const rows: Record<string, MarketplaceItem> = {};
      snap.forEach((row) => {
        rows[row.id] = normalizeItem(row.data(), row.id);
      });
      bySection.set(status, rows);
      emit();
    });
  });

  return () => unsubscribers.forEach((unsubscribe) => unsubscribe());
}

export async function getMarketplaceSellerHistoryCount(sellerId: string): Promise<number> {
  const uid = String(sellerId || '').trim();
  if (!uid) return 0;
  const q = query(
    collection(db, ITEMS),
    where('sellerId', '==', uid),
    where('status', 'in', ['sold', 'archived']),
  );
  const snap = await getCountFromServer(q);
  return Number(snap.data().count || 0);
}

export async function getMarketplaceSellerHistoryPage(
  sellerId: string,
  opts?: { pageSize?: number; cursor?: MarketplaceSellerHistoryCursor | null },
): Promise<{
  items: Record<string, MarketplaceItem>;
  cursor: MarketplaceSellerHistoryCursor | null;
  hasMore: boolean;
}> {
  const uid = String(sellerId || '').trim();
  if (!uid) return { items: {}, cursor: null, hasMore: false };

  const pageSize = Math.max(1, Math.min(Number(opts?.pageSize ?? 20), 50));
  const base = [
    where('sellerId', '==', uid),
    where('status', 'in', ['sold', 'archived']),
    orderBy('createdAt', 'desc'),
    orderBy(documentId(), 'desc'),
  ] as const;

  const q = opts?.cursor
    ? query(collection(db, ITEMS), ...base, startAfter(opts.cursor), limit(pageSize + 1))
    : query(collection(db, ITEMS), ...base, limit(pageSize + 1));

  const snap = await getDocs(q);
  const pageDocs = snap.docs.slice(0, pageSize);
  const items: Record<string, MarketplaceItem> = {};
  pageDocs.forEach((row) => {
    items[row.id] = normalizeItem(row.data(), row.id);
  });

  return {
    items,
    cursor: pageDocs.length ? pageDocs[pageDocs.length - 1] : null,
    hasMore: snap.docs.length > pageSize,
  };
}

export async function createMarketplaceItem(item: Partial<MarketplaceItem> & Record<string, any>) {
  assertBazaarListingsEnabled('Creating Bazaar listings');
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');

  const rawId = item.docId || item.id || Date.now();
  const docId = getItemDocId(rawId);
  const ref = doc(db, ITEMS, docId);
  const { priceCents, currency } = normalizeMoney(item);

  const title = String(item.title || item.name || '').trim();
  if (!title) throw new Error('Listing title is required.');
  if (!Number.isInteger(priceCents) || priceCents <= 0) throw new Error('Invalid listing price.');

  const payload = stripUndefined({
    ...item,
    id: docId,
    docId,
    title,
    name: title,
    sellerId: user.uid,
    sellerName: item.sellerName || user.displayName || 'Seller',
    quantity: Math.max(1, Math.floor(Number(item.quantity || 1))),
    priceCents,
    // Compatibility only. New code must treat priceCents as authoritative.
    price: priceCents / 100,
    currency,
    status: 'active',
    fulfillmentMethods:
      Array.isArray(item.fulfillmentMethods) && item.fulfillmentMethods.length
        ? item.fulfillmentMethods
        : ['LOCAL_PICKUP'],
    // Listings-only round: buyer/seller arrange payment directly.
    checkoutMethods: ['DIRECT'],
    createdAt: Number(item.createdAt || Date.now()),
    updatedAt: Date.now(),
    schemaVersion: 2,
  });

  await setDoc(ref, payload);
  return docId;
}

export async function updateMarketplaceItem(
  itemId: number | string,
  patch: Partial<MarketplaceItem> & Record<string, any>,
) {
  assertBazaarListingsEnabled('Updating Bazaar listings');
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');

  const ref = doc(db, ITEMS, getItemDocId(itemId));
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Listing not found.');
  if (String(snap.data()?.sellerId || '') !== user.uid) throw new Error('You do not own this listing.');

  const safePatch: Record<string, any> = {};
  const allowed = [
    'title',
    'name',
    'description',
    'priceCents',
    'currency',
    'category',
    'condition',
    'quantity',
    'imageUrls',
    'images',
    'imagePaths',
    'fulfillmentMethods',
    'locationLabel',
    'city',
    'region',
    'countryCode',
    'status',
    'archived',
    'archivedAt',
  ];

  for (const key of allowed) {
    if (patch[key] !== undefined) safePatch[key] = patch[key];
  }

  if (safePatch.priceCents !== undefined) {
    safePatch.priceCents = Math.max(0, Math.round(Number(safePatch.priceCents) || 0));
    safePatch.price = safePatch.priceCents / 100;
  }
  if (safePatch.currency !== undefined) {
    safePatch.currency = String(safePatch.currency || 'usd').toLowerCase();
  }
  if (safePatch.title !== undefined) {
    safePatch.title = String(safePatch.title).trim();
    safePatch.name = safePatch.title;
  }
  if (safePatch.status !== undefined && !['active', 'archived', 'cancelled'].includes(String(safePatch.status))) {
    throw new Error('That listing status is server-controlled.');
  }

  safePatch.checkoutMethods = ['DIRECT'];
  safePatch.updatedAt = Date.now();
  await updateDoc(ref, stripUndefined(safePatch));
}

export async function archiveMarketplaceItem(itemId: number | string) {
  return updateMarketplaceItem(itemId, {
    status: 'archived' as MarketplaceItemStatus,
    archived: true,
    archivedAt: Date.now(),
  } as any);
}

/**
 * Permanent seller delete is server-authoritative. The callable verifies ownership
 * and refuses hard deletion when transaction history exists.
 */
export async function sellerDeleteMarketplaceItem(itemId: number | string) {
  assertBazaarListingsEnabled('Deleting Bazaar listings');
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  const callable = httpsCallable<{ itemId: string }, { ok: boolean; deleted?: boolean }>(functions, 'sellerDeleteMarketplaceItem');
  const result = await callable({ itemId: getItemDocId(itemId) });
  return result.data;
}

export async function sellerSetMarketplaceItemPending(
  itemId: number | string,
  hold?: { threadId?: string; offerId?: string; buyerId?: string },
) {
  assertBazaarListingsEnabled('Marking Bazaar listings pending');
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  const callable = httpsCallable<
    { itemId: string; threadId?: string; offerId?: string; buyerId?: string },
    { ok: boolean; itemId: string; status: 'pending'; alreadyPending?: boolean }
  >(functions, 'sellerSetMarketplaceItemPending');
  const result = await callable({ itemId: getItemDocId(itemId), threadId: hold?.threadId, offerId: hold?.offerId, buyerId: hold?.buyerId });
  return result.data;
}

export async function sellerMakeMarketplaceItemAvailable(itemId: number | string) {
  assertBazaarListingsEnabled('Making Bazaar listings available');
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  const callable = httpsCallable<{ itemId: string }, { ok: boolean; itemId: string; status: 'active'; alreadyAvailable?: boolean }>(functions, 'sellerMakeMarketplaceItemAvailable');
  const result = await callable({ itemId: getItemDocId(itemId) });
  return result.data;
}

export async function sellerMarkMarketplaceItemSold(itemId: number | string) {
  assertBazaarListingsEnabled('Marking Bazaar listings sold');
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  const callable = httpsCallable<
    { itemId: string },
    { ok: boolean; itemId: string; status: 'sold'; alreadySold?: boolean }
  >(functions, 'sellerMarkMarketplaceItemSold');
  const result = await callable({ itemId: getItemDocId(itemId) });
  return result.data;
}

export async function deleteMarketplaceItem(itemId: number | string) {
  return sellerDeleteMarketplaceItem(itemId);
}

export async function setMarketplaceItemStatus(
  itemId: number | string,
  status: MarketplaceItemStatus,
  extraPatch?: Record<string, any>,
) {
  if (!['active', 'archived', 'cancelled'].includes(String(status))) {
    throw new Error('That listing status is server-controlled.');
  }
  return updateMarketplaceItem(itemId, { ...(extraPatch || {}), status } as any);
}

export async function upsertMarketplaceInquiry(
  itemId: number | string,
  input: { buyerName: string; buyerAvatar?: string | null; message: string },
) {
  assertBazaarListingsEnabled('Sending Bazaar inquiries');
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');

  const itemRef = doc(db, ITEMS, getItemDocId(itemId));
  const itemSnap = await getDoc(itemRef);
  if (!itemSnap.exists()) throw new Error('Listing not found.');
  if (String(itemSnap.data()?.sellerId || '') === user.uid) throw new Error('You cannot message yourself about your listing.');

  const offerRef = doc(itemRef, OFFERS_SUBCOLLECTION, user.uid);
  const existing = await getDoc(offerRef);
  const now = Date.now();
  const cleanMessage = String(input.message || '').trim().slice(0, 2000);
  if (!cleanMessage) throw new Error('Write a message first.');

  if (existing.exists()) {
    await updateDoc(offerRef, {
      message: cleanMessage,
      note: cleanMessage,
      status: 'pending',
      timestamp: now,
      updatedAt: now,
    });
  } else {
    await setDoc(offerRef, stripUndefined({
      id: user.uid,
      itemId: Number.isFinite(Number(itemId)) ? Number(itemId) : String(itemId),
      itemDocId: String(itemId),
      kind: 'inquiry',
      buyerId: user.uid,
      buyerName: String(input.buyerName || user.displayName || 'Angler').slice(0, 100),
      buyerAvatar: input.buyerAvatar || null,
      message: cleanMessage,
      note: cleanMessage,
      status: 'pending',
      timestamp: now,
      createdAt: now,
      updatedAt: now,
    }));
  }
  return user.uid;
}

export async function getMyMarketplaceInquiry(itemId: number | string) {
  const user = auth.currentUser;
  if (!user) return null;
  const ref = doc(db, ITEMS, getItemDocId(itemId), OFFERS_SUBCOLLECTION, user.uid);
  const snap = await getDoc(ref);
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as any) : null;
}

export async function replyToMarketplaceInquiry(
  itemId: number | string,
  buyerId: string,
  reply: string,
) {
  assertBazaarListingsEnabled('Replying to Bazaar inquiries');
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');

  const itemRef = doc(db, ITEMS, getItemDocId(itemId));
  const itemSnap = await getDoc(itemRef);
  if (!itemSnap.exists()) throw new Error('Listing not found.');
  if (String(itemSnap.data()?.sellerId || '') !== user.uid) throw new Error('Only the seller can reply.');

  const offerRef = doc(itemRef, OFFERS_SUBCOLLECTION, String(buyerId));
  await updateDoc(offerRef, {
    sellerReply: String(reply || '').trim().slice(0, 2000),
    sellerRespondedAt: Date.now(),
    status: 'responded',
    updatedAt: Date.now(),
  });
}

export async function addOfferToMarketplaceItem(itemId: number | string, offer: Offer) {
  return upsertMarketplaceInquiry(itemId, {
    buyerName: offer.buyerName,
    buyerAvatar: offer.buyerAvatar,
    message: offer.message || offer.note || '',
  });
}

export async function updateOfferStatus(
  itemId: number | string,
  offerId: string,
  status: OfferStatus,
) {
  assertBazaarListingsEnabled('Updating Bazaar inquiry');
  const ref = doc(db, ITEMS, getItemDocId(itemId), OFFERS_SUBCOLLECTION, offerId);
  await updateDoc(ref, { status, updatedAt: Date.now() });
}

export async function reportMarketplaceItem(input: {
  itemId: string;
  sellerId: string;
  reason: string;
  details?: string;
}) {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');

  await addDoc(collection(db, 'reports'), {
    reporterUid: user.uid,
    targetUserId: input.sellerId || null,
    targetType: 'bazaar_listing',
    reportType: 'bazaar_listing',
    listingId: String(input.itemId),
    itemId: String(input.itemId),
    reason: String(input.reason || 'other').slice(0, 80),
    details: String(input.details || '').trim().slice(0, 1200),
    status: 'open',
    createdAt: Date.now(),
    createdAtServer: serverTimestamp(),
  });
}

export function subscribeMarketplaceTransactionsForUser(
  userId: string,
  onTx: (txs: MarketplaceTransaction[]) => void,
  opts?: { role?: 'buyer' | 'seller' | 'any'; max?: number },
) {
  const role = opts?.role ?? 'any';
  const max = Math.max(1, Math.min(Number(opts?.max ?? 50), 100));

  const makeQuery = (field: 'buyerId' | 'sellerId') =>
    query(collection(db, TX), where(field, '==', userId), orderBy('createdAt', 'desc'), limit(max));

  const mapRows = (snap: any) =>
    snap.docs.map((d: any) => ({ id: d.id, ...(d.data() as any) }) as MarketplaceTransaction);

  if (role === 'buyer') return onSnapshot(makeQuery('buyerId'), (snap) => onTx(mapRows(snap)));
  if (role === 'seller') return onSnapshot(makeQuery('sellerId'), (snap) => onTx(mapRows(snap)));

  let buyerTxs: MarketplaceTransaction[] = [];
  let sellerTxs: MarketplaceTransaction[] = [];
  const emit = () => {
    const byId = new Map<string, MarketplaceTransaction>();
    [...buyerTxs, ...sellerTxs].forEach((tx) => tx?.id && byId.set(tx.id, tx));
    onTx(Array.from(byId.values()).sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0)));
  };

  const unsubBuyer = onSnapshot(makeQuery('buyerId'), (snap) => {
    buyerTxs = mapRows(snap);
    emit();
  });
  const unsubSeller = onSnapshot(makeQuery('sellerId'), (snap) => {
    sellerTxs = mapRows(snap);
    emit();
  });

  return () => {
    unsubBuyer();
    unsubSeller();
  };
}

export async function getMarketplaceTransaction(txId: string) {
  const snap = await getDoc(doc(db, TX, txId));
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as MarketplaceTransaction) : null;
}

export async function getLatestTransactionForItem(itemId: number | string) {
  const q = query(collection(db, TX), where('itemId', '==', String(itemId)), orderBy('createdAt', 'desc'), limit(1));
  const snap = await getDocs(q);
  return snap.empty ? null : ({ id: snap.docs[0].id, ...snap.docs[0].data() } as MarketplaceTransaction);
}
